import React, { createContext, useContext, useState, useEffect } from 'react';

const ModeContext = createContext();

export const MODES = {
    CRYPTO: 'crypto',
    FIAT: 'fiat'
};

export const CURRENCIES = {
    crypto: [
        { code: 'USDT', name: 'Tether', symbol: '$' },
        { code: 'BTC', name: 'Bitcoin', symbol: '₿' },
        { code: 'ETH', name: 'Ethereum', symbol: 'Ξ' },
        { code: 'BNB', name: 'Binance Coin', symbol: 'BNB' },
        { code: 'SOL', name: 'Solana', symbol: 'SOL' }
    ],
    fiat: [
        { code: 'TRY', name: 'Türk Lirası', symbol: '₺' },
        { code: 'USD', name: 'US Dollar', symbol: '$' },
        { code: 'EUR', name: 'Euro', symbol: '€' },
        { code: 'GBP', name: 'British Pound', symbol: '£' }
    ]
};

export function ModeProvider({ children }) {
    const [mode, setModeState] = useState(() => {
        try {
            const saved = localStorage.getItem('getiri_mode');
            return saved || null; // null means no mode selected yet
        } catch {
            return null;
        }
    });

    const [selectedCurrency, setSelectedCurrency] = useState(() => {
        try {
            const saved = localStorage.getItem('getiri_currency');
            return saved || 'USDT';
        } catch {
            return 'USDT';
        }
    });

    // Apply mode to document
    useEffect(() => {
        if (mode) {
            document.documentElement.setAttribute('data-mode', mode);
            localStorage.setItem('getiri_mode', mode);

            // Set default currency when mode changes
            if (mode === MODES.CRYPTO && !CURRENCIES.crypto.find(c => c.code === selectedCurrency)) {
                setSelectedCurrency('USDT');
            } else if (mode === MODES.FIAT && !CURRENCIES.fiat.find(c => c.code === selectedCurrency)) {
                setSelectedCurrency('TRY');
            }
        } else {
            document.documentElement.removeAttribute('data-mode');
        }
    }, [mode, selectedCurrency]);

    // Save currency to localStorage
    useEffect(() => {
        localStorage.setItem('getiri_currency', selectedCurrency);
    }, [selectedCurrency]);

    const setMode = (newMode) => {
        if (newMode === MODES.CRYPTO || newMode === MODES.FIAT) {
            setModeState(newMode);
        }
    };

    const clearMode = () => {
        setModeState(null);
        localStorage.removeItem('getiri_mode');
        document.documentElement.removeAttribute('data-mode');
    };

    const getCurrencies = () => {
        return mode ? CURRENCIES[mode] : [];
    };

    const getCurrentCurrency = () => {
        const currencies = getCurrencies();
        return currencies.find(c => c.code === selectedCurrency) || currencies[0];
    };

    const isCryptoMode = mode === MODES.CRYPTO;
    const isFiatMode = mode === MODES.FIAT;
    const hasSelectedMode = mode !== null;

    const value = {
        mode,
        setMode,
        clearMode,
        selectedCurrency,
        setSelectedCurrency,
        getCurrencies,
        getCurrentCurrency,
        isCryptoMode,
        isFiatMode,
        hasSelectedMode,
        MODES,
        CURRENCIES
    };

    return (
        <ModeContext.Provider value={value}>
            {children}
        </ModeContext.Provider>
    );
}

export function useMode() {
    const context = useContext(ModeContext);
    if (!context) {
        throw new Error('useMode must be used within a ModeProvider');
    }
    return context;
}
